import { Link } from 'react-router-dom';
import type { DifficultyDayState, StreakView } from '../../api/puzzles';
import { LetterTiles } from '../ui/LetterTiles';

// Accent bars borrow premium-square colours from the board (decorative);
// buttons use deeper shades of the same hue so white text keeps WCAG contrast.
const ACCENTS: Record<string, { bar: string; cta: string }> = {
  EASY: { bar: 'bg-board-dl', cta: 'bg-emerald-800 hover:bg-emerald-900 border-emerald-950' },
  MEDIUM: { bar: 'bg-board-tl', cta: 'bg-blue-800 hover:bg-blue-900 border-blue-950' },
  HARD: { bar: 'bg-board-tw', cta: 'bg-red-800 hover:bg-red-900 border-red-950' },
};

const STATE_BADGES: Record<DifficultyDayState['state'], { label: string; className: string }> = {
  not_started: { label: 'Not started', className: 'bg-stone-200/70 text-stone-600' },
  in_progress: { label: 'In progress', className: 'bg-amber-200/80 text-amber-900' },
  won: { label: 'Won ✓', className: 'bg-emerald-800 text-white' },
  lost: { label: 'Lost ✗', className: 'bg-red-800 text-white' },
};

// Only the playable states use a single labelled board link. Finished states
// (won/lost) render their own buttons below — see the CTA block.
const PLAY_LABELS: Partial<Record<DifficultyDayState['state'], string>> = {
  not_started: 'Play',
  in_progress: 'Continue',
};

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="px-2 py-2.5">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-600">
        {label}
      </dt>
      <dd className="mt-0.5 text-xl font-bold tabular-nums text-stone-900">{value}</dd>
    </div>
  );
}

export function DifficultyCard({
  day,
  streak,
  date,
  authed,
  claimedSignedOut = false,
}: {
  day: DifficultyDayState;
  streak: StreakView | null;
  /** Puzzle date (today), used to link finished cards to their replay. */
  date: string | null;
  /** Signed in? Guests have no server-side history (see resultHref below). */
  authed: boolean;
  /**
   * Signed out, but this browser's guest day has already been handed to an
   * account. Guest play cannot reach the lives now sitting on that account,
   * so the card points at signing in rather than offering a board that could
   * never be saved.
   */
  claimedSignedOut?: boolean;
}) {
  const badge = claimedSignedOut
    ? // Says something about the BOARD, not about the viewer. "Signed out" was
      // the viewer's state repeated on all three cards, which read as three
      // separate faults rather than one explained situation.
      { label: 'On your account', className: 'bg-stone-800 text-white' }
    : day.locked
      ? { label: '🔒 Locked', className: 'bg-stone-800 text-white' }
      : STATE_BADGES[day.state];
  const accent = ACCENTS[day.difficulty] ?? ACCENTS.EASY!;
  const playable = day.puzzle !== null && !day.locked && !claimedSignedOut;
  const finished = day.state === 'won' || day.state === 'lost';
  const streakCount = streak?.current_streak ?? 0;

  const diff = day.difficulty.toLowerCase();
  const playHref = `/play/${diff}`;
  // "View result" → the move-by-move replay for signed-in users. Guests have no
  // server history; returning to the board restores their finished life from
  // localStorage, so for them "View result" points back at /play.
  const replayHref = date ? `/history/${date}/${diff}` : '/history';
  const resultHref = authed ? replayHref : playHref;
  // A won puzzle can be replayed with leftover lives for a better score — but
  // only server-side; a guest returning to the board just sees their result.
  const canKeepPlaying = authed && day.state === 'won' && day.lives_remaining > 0;

  const baseBtn =
    'block rounded-md border-b-4 px-4 py-2.5 text-center text-sm font-bold uppercase tracking-[0.18em] active:translate-y-px active:border-b-2';
  const primaryBtn = `${baseBtn} text-white ${accent.cta}`;
  const secondaryBtn = `${baseBtn} border-stone-300 bg-white text-stone-600 hover:bg-stone-50`;

  return (
    <article
      data-testid={`difficulty-card-${day.difficulty.toLowerCase()}`}
      className="overflow-hidden rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]"
    >
      <div className={`h-1.5 ${accent.bar}`} aria-hidden="true" />

      <div className="flex items-center justify-between px-4 pt-3.5">
        <h2 className="leading-none">
          <LetterTiles word={day.difficulty} size="sm" label={day.difficulty.toLowerCase()} />
        </h2>
        <span
          className={`rounded-sm px-2 py-1 text-[11px] font-bold uppercase tracking-wider ${badge.className}`}
        >
          {badge.label}
        </span>
      </div>

      <dl className="mx-4 mt-3.5 grid grid-cols-3 divide-x divide-tile-edge/60 rounded-md border border-tile-edge/60 bg-board-bg/40 text-center">
        <Stat
          label="Tiles"
          value={
            day.puzzle
              ? 'tile_count' in day.puzzle
                ? day.puzzle.tile_count
                : day.puzzle.daily_bag.length
              : '—'
          }
        />
        <Stat label="Moves" value={day.puzzle?.move_count ?? '—'} />
        <Stat label="Target" value={day.target_score ?? '—'} />
      </dl>

      <div className="flex items-center justify-between px-4 pt-3">
        <p className="flex items-center gap-2 text-sm text-stone-600">
          <span
            aria-hidden="true"
            className="inline-flex h-6 w-6 items-center justify-center rounded border-b-2 border-tile-edge bg-tile-face text-xs font-bold text-stone-900"
          >
            {streakCount}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-600">
            Day streak
          </span>
          <span className="sr-only">Streak: {streakCount}</span>
        </p>
        {day.state === 'in_progress' || day.state === 'lost' ? (
          <p className="text-sm tabular-nums text-stone-600">
            {day.best_score > 0 ? `Best ${day.best_score}` : null}
            {day.state === 'in_progress' ? ` · ${day.lives_remaining} lives left` : null}
          </p>
        ) : null}
      </div>

      <div className="p-4">
        {claimedSignedOut ? (
          // No CTA here: the banner above the cards carries it once. Repeating
          // "Sign in to continue" on all three cards (plus the footer) put the
          // same prompt on screen four times.
          <p className="text-center text-sm text-stone-600">Saved to your account.</p>
        ) : day.locked ? (
          <div className="space-y-2">
            <p className="text-center text-sm text-stone-600">You've used today's free board.</p>
            <Link to="/register" className={primaryBtn}>
              Create free account
            </Link>
          </div>
        ) : !playable ? (
          <p className="rounded-md bg-stone-100 px-4 py-2.5 text-center text-sm text-stone-500">
            Today's puzzle couldn't be generated — back tomorrow.
          </p>
        ) : !finished ? (
          <Link to={playHref} className={primaryBtn}>
            {PLAY_LABELS[day.state]}
          </Link>
        ) : canKeepPlaying ? (
          // Won with lives to spare: let them push for a higher score, or
          // review what they already played.
          <div className="flex flex-col gap-2 sm:flex-row">
            <Link to={playHref} className={`${primaryBtn} sm:flex-1`}>
              Keep playing
            </Link>
            <Link to={resultHref} className={`${secondaryBtn} sm:flex-1`}>
              View result
            </Link>
          </div>
        ) : (
          // Lost, or won with no lives left — only a result to review.
          <Link to={resultHref} className={secondaryBtn}>
            View result
          </Link>
        )}
      </div>
    </article>
  );
}
